<template>
  <div class="mb-4 flex justify-end py-2">
    <DateFilter
      :model-value="timePeriod"
      :time-period-label="timePeriodLabel"
      :range-type="'month'"
      @update:model-value="setTimePeriod"
    />
  </div>

  <div
    v-if="data"
    class="mb-4 grid w-full grid-cols-2 gap-2 sm:grid-cols-4"
  >
    <div
      v-for="stat in summary"
      :key="stat.label"
      class="rounded-lg bg-background p-3 shadow ring-background-fg/8"
    >
      <div
        class="text-xs font-medium text-background-fg/70"
      >
        {{ stat.label }}
      </div>
      <div class="mt-1 text-2xl font-bold text-primary">
        {{ stat.value }}
      </div>
    </div>
  </div>

  <BaseCard class="relative">
    <BaseInnerLoading v-if="isLoading" />

    <div
      v-else-if="isError"
      class="flex h-full flex-col items-center justify-center p-4"
    >
      <IconExclamationCircleFilled
        class="mb-2 text-3xl text-error"
      />
      <span class="font-semibold">
        Failed to load merchant transaction totals
      </span>

      <FormSubmitButton
        type="button"
        class="mt-2"
        @click="() => refetch()"
      >
        Retry
      </FormSubmitButton>
    </div>

    <div v-else class="flex w-full flex-col">
      <div class="flex items-center justify-between p-2">
        <ToggleSwitch
          v-model="graphView"
          :options="graphViewOptions"
          selected-option-color="primary"
          text-size="sm"
        />
        <ButtonExport
          :is-loading="isExporting"
          :default-file-export-format="'csv'"
          @export="onExport"
        />
      </div>

      <div
        class="h-64 w-full overflow-hidden px-2 sm:h-96 md:h-[32rem]"
      >
        <ChartLineDate
          v-if="graphView === 'trend'"
          id="merchant-group-transactions-by-merchant-graph"
          :labels="labels"
          :datasets="datasets"
          granularity="day"
          :is-cumulative="false"
          :currency="currency"
          :isolated-dataset-index="isolatedDatasetIndex"
          :hovered-dataset-index="hoveredDatasetIndex"
          show-tooltip
        />
        <ChartBarDateStacked
          v-else-if="graphView === 'stacked'"
          id="merchant-group-transactions-by-merchant-stacked-graph"
          :labels="labels"
          :datasets="datasets"
          granularity="day"
          :currency="currency"
          :isolated-dataset-index="isolatedDatasetIndex"
          :hovered-dataset-index="hoveredDatasetIndex"
        />
      </div>

      <div
        class="flex flex-wrap items-center gap-2 border-t border-background-fg/10 p-3"
      >
        <button
          v-for="(
            merchant, index
          ) in data?.merchantDatasets"
          :key="merchant.merchantId"
          type="button"
          class="flex cursor-pointer items-center gap-2 rounded-lg border px-2.5 py-1.5 text-sm transition-all hover:bg-background-fg/5"
          :class="{
            'opacity-40 hover:opacity-60':
              isolatedDatasetIndex !== null &&
              isolatedDatasetIndex !== index,
            'font-medium': isolatedDatasetIndex === index,
          }"
          :style="{
            borderColor:
              isolatedDatasetIndex === index
                ? resolvedChartColors[
                    index % resolvedChartColors.length
                  ]
                : 'transparent',
          }"
          @click="onLegendClick(index)"
          @mouseenter="onLegendMouseenter(index)"
          @mouseleave="onLegendMouseleave()"
        >
          <span
            class="size-2.5 shrink-0 rounded-full"
            :style="{
              backgroundColor:
                resolvedChartColors[
                  index % resolvedChartColors.length
                ],
            }"
          />
          <span>{{ merchant.merchantName }}</span>
        </button>

        <button
          v-if="isolatedDatasetIndex !== null"
          type="button"
          class="ml-auto text-xs text-background-fg/40 transition-colors hover:text-background-fg/80"
          @click="isolatedDatasetIndex = null"
        >
          Show all
        </button>
      </div>
    </div>
  </BaseCard>

  <BaseCard v-if="data" class="mt-4">
    <BaseTable
      v-model:ordering="tableOrdering"
      :rows="sortedTableRows"
      :columns="tableColumns"
      :get-row-class="getTableRowClass"
    >
      <template #cell-merchantName="{ row }">
        <span class="flex items-center gap-2">
          <span
            class="size-2.5 shrink-0 rounded-full"
            :style="{
              backgroundColor:
                resolvedChartColors[
                  row.index % resolvedChartColors.length
                ],
            }"
          />
          <span>{{ row.merchantName }}</span>
        </span>
      </template>
    </BaseTable>
  </BaseCard>
</template>

<script setup lang="ts">
import type {
  TableColumn,
  TableOrdering,
} from "@/components/BaseTable.vue"

const resolvedChartColors = computed(() =>
  useColorOptions().options.map(
    ({ value }) => `rgb(${useCssVar(value).value})`
  )
)

const merchantGroup = useMerchantGroup()
const { formatToCurrencyString } = useCurrency()

const timePeriod = ref<MGTransactionsByMerchantReportQuery>(
  {
    start: DateFns.format(
      DateFns.startOfMonth(new Date()),
      "yyyy-MM-dd"
    ),
    end: DateFns.format(
      DateFns.endOfMonth(new Date()),
      "yyyy-MM-dd"
    ),
  }
)

const setTimePeriod = (
  value: MGTransactionsByMerchantReportQuery
) => {
  timePeriod.value = value
}

const timePeriodLabel = computed(() =>
  getLabelForRange(
    DateFns.parseISO(timePeriod.value.start),
    DateFns.parseISO(timePeriod.value.end)
  )
)

const { data, isLoading, isError, refetch } =
  useMGTransactionsByMerchantReport(timePeriod)

const currency = computed(
  () => merchantGroup.value?.currency ?? "GBP"
)

const labels = computed(() => [
  ...(data.value?.labels ?? []),
])

const datasets = computed(() =>
  (data.value?.merchantDatasets ?? []).map(
    (merchant, index) => ({
      label: merchant.merchantName,
      data: [...merchant.data],
      color:
        resolvedChartColors.value[
          index % resolvedChartColors.value.length
        ],
    })
  )
)

type TableRow = {
  merchantName: string
  index: number
  total: number
  share: number
  activeDays: number
  totalDays: number
  bestDay: number
  dailyAvg: number
}

type TableColumnId =
  | "merchantName"
  | "total"
  | "share"
  | "activeDays"
  | "bestDay"
  | "dailyAvg"

const tableColumns: Array<
  TableColumn<TableRow, TableColumnId, TableColumnId>
> = [
  {
    id: "merchantName",
    header: "Merchant",
    getCellValue: (row) => row.merchantName,
    isSortable: true,
  },
  {
    id: "total",
    header: "Total",
    getCellValue: (row) =>
      formatToCurrencyString(row.total, currency.value),
    align: "right",
    autoWidth: true,
    isSortable: true,
  },
  {
    id: "share",
    header: "Share",
    getCellValue: (row) =>
      `${(row.share * 100).toFixed(1)}%`,
    align: "right",
    autoWidth: true,
    isSortable: true,
  },
  {
    id: "activeDays",
    header: "Active Days",
    getCellValue: (row) =>
      `${row.activeDays} / ${row.totalDays}`,
    align: "right",
    autoWidth: true,
    isSortable: true,
  },
  {
    id: "bestDay",
    header: "Best Day",
    getCellValue: (row) =>
      formatToCurrencyString(row.bestDay, currency.value),
    align: "right",
    autoWidth: true,
    isSortable: true,
  },
  {
    id: "dailyAvg",
    header: "Daily Avg",
    getCellValue: (row) =>
      formatToCurrencyString(row.dailyAvg, currency.value),
    align: "right",
    autoWidth: true,
    isSortable: true,
  },
]

const tableOrdering = ref<
  TableOrdering<TableColumnId> | undefined
>("-total")

const summaryStats = computed(() => {
  const labelsLen = data.value?.labels?.length ?? 0
  const merchantDatasets =
    data.value?.merchantDatasets ?? []

  const totalRevenue = merchantDatasets.reduce(
    (acc, ds) =>
      acc +
      (ds.data?.reduce((s, v) => s + (v ?? 0), 0) ?? 0),
    0
  )

  const activeMerchants = merchantDatasets.filter((ds) =>
    ds.data.some((v) => v > 0)
  ).length

  const dailySums: number[] = []
  for (let i = 0; i < labelsLen; i++) {
    let daySum = 0
    for (const ds of merchantDatasets) {
      daySum += ds.data?.[i] ?? 0
    }
    dailySums.push(daySum)
  }

  const bestDayValue = dailySums.length
    ? Math.max(...dailySums)
    : 0
  const totalMerchants = merchantDatasets.length
  const dailyAvg = labelsLen
    ? Math.round(totalRevenue / labelsLen)
    : 0

  return {
    totalRevenue,
    activeMerchants,
    totalMerchants,
    bestDayValue,
    dailyAvg,
  }
})

const summary = computed(() => {
  const currencyCode = currency.value ?? "GBP"

  if (
    !data.value ||
    (data.value.merchantDatasets?.length ?? 0) === 0
  ) {
    return [
      { label: "Total Revenue", value: "-" },
      { label: "Active Merchants", value: "-" },
      { label: "Best Day", value: "-" },
      { label: "Daily Avg", value: "-" },
    ]
  }

  return [
    {
      label: "Total Revenue",
      value: formatToCurrencyString(
        summaryStats.value.totalRevenue,
        currencyCode
      ),
    },
    {
      label: "Active Merchants",
      value: `${summaryStats.value.activeMerchants}/${summaryStats.value.totalMerchants}`,
    },
    {
      label: "Best Day",
      value: formatToCurrencyString(
        summaryStats.value.bestDayValue,
        currencyCode
      ),
    },
    {
      label: "Daily Avg",
      value: formatToCurrencyString(
        summaryStats.value.dailyAvg,
        currencyCode
      ),
    },
  ]
})

const tableRows = computed(() => {
  const merchantDatasets =
    data.value?.merchantDatasets ?? []
  const totalDays = data.value?.labels.length ?? 0
  const groupTotal = merchantDatasets.reduce(
    (sum, dataset) =>
      sum + dataset.data.reduce((s, v) => s + v, 0),
    0
  )

  return merchantDatasets.map((dataset, index) => {
    const total = dataset.data.reduce((s, v) => s + v, 0)
    const activeDays = dataset.data.filter(
      (v) => v > 0
    ).length
    const bestDay =
      dataset.data.length > 0
        ? Math.max(...dataset.data)
        : 0
    const dailyAvg =
      activeDays > 0 ? Math.round(total / activeDays) : 0
    const share = groupTotal > 0 ? total / groupTotal : 0

    return {
      merchantName: dataset.merchantName,
      index,
      total,
      share,
      activeDays,
      totalDays,
      bestDay,
      dailyAvg,
    }
  })
})

const sortedTableRows = computed(() => {
  const rows = [...tableRows.value]
  const orderingKey = tableOrdering.value
  if (!orderingKey) return rows
  const descending = orderingKey.startsWith("-")
  const columnIdToSortBy = (
    descending ? orderingKey.slice(1) : orderingKey
  ) as TableColumnId
  return rows.sort((a, b) => {
    const aVal = a[columnIdToSortBy]
    const bVal = b[columnIdToSortBy]

    if (
      typeof aVal === "number" &&
      typeof bVal === "number"
    ) {
      return descending ? bVal - aVal : aVal - bVal
    }
    const cmp = String(aVal).localeCompare(String(bVal))
    return descending ? -cmp : cmp
  })
})

const isolatedDatasetIndex = ref<number | null>(null)
const onLegendClick = (index: number) => {
  isolatedDatasetIndex.value =
    isolatedDatasetIndex.value === index ? null : index
}

const hoveredDatasetIndex = ref<number | null>(null)
const onLegendMouseenter = (index: number) => {
  if (isolatedDatasetIndex.value !== null) {
    return
  }
  hoveredDatasetIndex.value = index
}
const onLegendMouseleave = () => {
  hoveredDatasetIndex.value = null
}

const getTableRowClass = (row: TableRow) => {
  if (
    isolatedDatasetIndex.value === row.index ||
    (isolatedDatasetIndex.value === null &&
      hoveredDatasetIndex.value === row.index) ||
    (isolatedDatasetIndex.value == null &&
      hoveredDatasetIndex.value === null)
  ) {
    return "transition-opacity"
  }

  return "opacity-40 transition-opacity"
}

type GraphView = "trend" | "stacked"
const graphView = ref<GraphView>("trend")
const graphViewOptions = [
  { label: "Trend", value: "trend" },
  { label: "Stacked", value: "stacked" },
] as const

watch(graphView, () => {
  isolatedDatasetIndex.value = null
  hoveredDatasetIndex.value = null
})

const isExporting = ref(false)
const onExport = async (format?: FileExportFormat) => {
  if (isExporting.value) return
  isExporting.value = true
  try {
    await exportMGTransactionsByMerchantReport(
      timePeriod.value,
      format
    )
  } catch {
    toast.error(
      "Failed to export merchant transaction totals",
      3000
    )
  } finally {
    isExporting.value = false
  }
}
</script>
